import { useEffect, useState, useCallback } from "react";

import { ArrowLeft, FolderOpen, Archive, Tag as TagIcon, Search, ArrowUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { MomentumIndicator } from "@/components/dashboard/MomentumIndicator";
import { MemoryManager } from "@/components/memories/MemoryManager";
import { FolderTree } from "@/components/projects/FolderTree";
import { TagManager } from "@/components/projects/TagManager";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useProjectMomentum } from "@/hooks/useProjectMomentum";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface Folder {
  id: string;
  name: string;
  parent_folder_id: string | null;
  depth: number;
  projectCount?: number;
}

interface Project {
  id: string;
  name: string;
  board_id: string | null;
  folder_id: string | null;
  created_at: string;
  archived: boolean;
  boards?: {
    name: string;
  };
  tags?: Tag[];
}

const Projects = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Enable keyboard shortcuts for back navigation
  useKeyboardNavigation({
    onBack: () => navigate(ROUTES.DASHBOARD),
  });
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "created" | "updated">("created");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const fetchProjects = useCallback(async () => {
    if (!user) return;
    try {
      const { data: workspaces } = await supabase.from("workspaces").select("id").eq("user_id", user.id);

      if (!workspaces || workspaces.length === 0) {
        setProjects([]);
        return;
      }

      const wId = workspaces[0].id;
      setWorkspaceId(wId);

      // Fetch folders
      const { data: foldersData } = await supabase.from("folders").select("*").eq("workspace_id", wId).order("name");

      setFolders(foldersData || []);

      // Fetch tags
      const { data: tagsData } = await supabase.from("tags").select("*").eq("workspace_id", wId).order("name");

      setTags(tagsData || []);

      // Fetch all projects across all boards in the workspace
      const { data: boardsData } = await supabase.from("boards").select("id").eq("workspace_id", wId);

      if (!boardsData || boardsData.length === 0) {
        setProjects([]);
        return;
      }

      const boardIds = boardsData.map((b) => b.id);

      const { data: projectsData } = await supabase
        .from("projects")
        .select(
          `
          id,
          name,
          board_id,
          folder_id,
          created_at,
          archived,
          boards (
            name
          )
        `,
        )
        .or(`board_id.is.null,board_id.in.(${boardIds.join(",")})`)
        .order("created_at", { ascending: false });

      // Fetch project tags
      if (projectsData && projectsData.length > 0) {
        const projectIds = projectsData.map((p) => p.id);
        const { data: projectTagsData } = await supabase
          .from("project_tags")
          .select(
            `
            project_id,
            tags (
              id,
              name,
              color
            )
          `,
          )
          .in("project_id", projectIds);

        // Group tags by project
        const tagsByProject = new Map<string, Tag[]>();
        projectTagsData?.forEach((pt: any) => {
          if (!tagsByProject.has(pt.project_id)) {
            tagsByProject.set(pt.project_id, []);
          }
          if (pt.tags) {
            tagsByProject.get(pt.project_id)!.push(pt.tags);
          }
        });

        const enrichedProjects = projectsData.map((p) => ({
          ...p,
          tags: tagsByProject.get(p.id) || [],
        }));

        setProjects(enrichedProjects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [user]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const toggleTag = (tagId: string) => {
    const newSelection = new Set(selectedTagIds);
    if (newSelection.has(tagId)) {
      newSelection.delete(tagId);
    } else {
      newSelection.add(tagId);
    }
    setSelectedTagIds(newSelection);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedFolderId(null);
    setSelectedTagIds(new Set());
    setShowArchived(false);
  };

  const filteredAndSortedProjects = projects
    .filter((p) => {
      // Filter by archived status
      if (!showArchived && p.archived) return false;

      // Filter by search query (search in name and board name)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const nameMatch = p.name.toLowerCase().includes(query);
        const boardMatch = p.boards?.name?.toLowerCase().includes(query);
        if (!nameMatch && !boardMatch) return false;
      }

      // Filter by folder
      if (selectedFolderId !== null && p.folder_id !== selectedFolderId) return false;

      // Filter by tags (show if ANY selected tag matches)
      if (selectedTagIds.size > 0) {
        const projectTagIds = new Set(p.tags?.map((t) => t.id) || []);
        const hasAnyTag = Array.from(selectedTagIds).some((id) => projectTagIds.has(id));
        if (!hasAnyTag) return false;
      }

      return true;
    })
    .sort((a, b) => {
      let comparison = 0;

      if (sortBy === "name") {
        comparison = a.name.localeCompare(b.name);
      } else if (sortBy === "created") {
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

  const orphanedProjects = filteredAndSortedProjects.filter((p) => !p.board_id);
  const boardedProjects = filteredAndSortedProjects.filter((p) => p.board_id);
  const archivedCount = projects.filter((p) => p.archived).length;
  const hasActiveFilters = searchQuery.trim() || selectedFolderId !== null || selectedTagIds.size > 0 || showArchived;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.DASHBOARD)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="grid md:grid-cols-[240px_1fr] gap-6">
          {/* Sidebar */}
          <aside className="space-y-6">
            {workspaceId && (
              <>
                <FolderTree
                  folders={folders}
                  selectedFolderId={selectedFolderId}
                  onSelectFolder={setSelectedFolderId}
                  workspaceId={workspaceId}
                  onRefresh={fetchProjects}
                />

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <TagIcon className="h-4 w-4" />
                      Filter by Tags
                    </h3>
                    <TagManager tags={tags} workspaceId={workspaceId} onRefresh={fetchProjects} />
                  </div>
                  <div className="space-y-1">
                    {tags.map((tag) => (
                      <div key={tag.id} className="flex items-center gap-1 group">
                        <button
                          onClick={() => toggleTag(tag.id)}
                          className={`flex-1 text-left py-1 px-2 rounded-md hover:bg-accent/50 transition-colors ${
                            selectedTagIds.has(tag.id) ? "bg-accent" : ""
                          }`}
                        >
                          <Badge
                            style={{
                              backgroundColor: tag.color,
                              color: "#ffffff",
                            }}
                          >
                            {tag.name}
                          </Badge>
                        </button>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <MemoryManager tagId={tag.id} scopeLabel={tag.name} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </aside>

          {/* Main content */}
          <div>
            <div className="mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-semibold mb-2">All Projects</h1>
                  <p className="text-muted-foreground">
                    {filteredAndSortedProjects.length} {filteredAndSortedProjects.length === 1 ? "project" : "projects"}
                    {searchQuery && " matching search"}
                    {selectedFolderId && " in selected folder"}
                    {selectedTagIds.size > 0 && ` with ${selectedTagIds.size} tag filter(s)`}
                  </p>
                </div>
                {archivedCount > 0 && (
                  <div className="flex items-center space-x-2">
                    <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
                    <Label htmlFor="show-archived" className="flex items-center gap-2 cursor-pointer">
                      <Archive className="h-4 w-4" />
                      Show archived ({archivedCount})
                    </Label>
                  </div>
                )}
              </div>

              {/* Search and Sort Controls */}
              <div className="flex gap-3 items-center">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <div className="flex gap-2 items-center">
                  <Label className="text-sm text-muted-foreground">Sort by:</Label>
                  <Select value={sortBy} onValueChange={(value: "name" | "created") => setSortBy(value)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="created">Date Created</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={toggleSortOrder}
                    title={sortOrder === "asc" ? "Ascending" : "Descending"}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </div>

                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
              </div>
            </div>

            {filteredAndSortedProjects.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">
                    {projects.length === 0 ? "No projects yet" : "No projects match the current filters"}
                  </p>
                  {projects.length === 0 && (
                    <Button onClick={() => navigate(ROUTES.BOARDS_CREATE)}>Create your first board</Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-8">
                {orphanedProjects.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold mb-4 text-amber-600 dark:text-amber-500">
                      Projects Without a Board
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2">
                      {orphanedProjects.map((project) => (
                        <Card
                          key={project.id}
                          className={`cursor-pointer hover:shadow-md transition-shadow border-amber-200 dark:border-amber-900 ${
                            project.archived ? "opacity-60" : ""
                          }`}
                          onClick={() => navigate(`/projects/${project.id}`)}
                        >
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              {project.name}
                              {project.archived && <Archive className="h-4 w-4 text-muted-foreground" />}
                            </CardTitle>
                            <CardDescription className="text-amber-600 dark:text-amber-500">
                              {project.archived ? "Archived • No board assigned" : "No board assigned"}
                            </CardDescription>
                            {project.tags && project.tags.length > 0 && (
                              <div className="flex gap-1 flex-wrap mt-2">
                                {project.tags.map((tag) => (
                                  <Badge
                                    key={tag.id}
                                    style={{
                                      backgroundColor: tag.color,
                                      color: "#ffffff",
                                    }}
                                    className="text-xs"
                                  >
                                    {tag.name}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {boardedProjects.length > 0 && (
                  <div>
                    <h2 className="text-xl font-semibold mb-4">Projects in Boards</h2>
                    <div className="grid gap-4 md:grid-cols-2">
                      {boardedProjects.map((project) => (
                        <Card
                          key={project.id}
                          className={`cursor-pointer hover:shadow-md transition-shadow ${
                            project.archived ? "opacity-60" : ""
                          }`}
                          onClick={() => navigate(`/projects/${project.id}`)}
                        >
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              {project.name}
                              {project.archived && <Archive className="h-4 w-4 text-muted-foreground" />}
                            </CardTitle>
                            <CardDescription>
                              {project.archived && "Archived • "}
                              Board: {project.boards?.name || "Unknown"}
                            </CardDescription>
                            {project.tags && project.tags.length > 0 && (
                              <div className="flex gap-1 flex-wrap mt-2">
                                {project.tags.map((tag) => (
                                  <Badge
                                    key={tag.id}
                                    style={{
                                      backgroundColor: tag.color,
                                      color: "#ffffff",
                                    }}
                                    className="text-xs"
                                  >
                                    {tag.name}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Projects;
